import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChevronDown, Scale, Clock } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

export interface DecisionLogEntryData {
  readonly id: string
  readonly decision: string
  readonly subjectId?: string
  readonly resourceId?: string
  readonly action?: string
  readonly policyName?: string
  readonly reason?: string
  readonly timestamp: string
}

export interface DecisionLogCardData {
  readonly message: string
  readonly entries: readonly DecisionLogEntryData[]
  readonly pagination?: {
    readonly page: number
    readonly totalItems: number
    readonly pageSize: number
  }
}

const DECISION_STYLES: Record<string, string> = {
  Permit: 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50',
  Deny: 'border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50',
  NotApplicable: 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50',
  Indeterminate: 'border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50',
}

const MAX_VISIBLE = 8

export function DecisionLogCard({ data }: { data: DecisionLogCardData }) {
  const [expanded, setExpanded] = useState(false)

  if (data.entries.length === 0) {
    return (
      <div className="my-2 rounded-lg border border-border dark:border-border/40 bg-card shadow-sm px-3.5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Scale className="size-4" />
          <span>{data.message}</span>
        </div>
      </div>
    )
  }

  const needsCollapse = data.entries.length > MAX_VISIBLE
  const visible = needsCollapse && !expanded ? data.entries.slice(0, MAX_VISIBLE) : data.entries
  const hiddenCount = data.entries.length - MAX_VISIBLE

  // Count decisions
  const counts: Record<string, number> = {}
  for (const e of data.entries) {
    counts[e.decision] = (counts[e.decision] ?? 0) + 1
  }

  return (
    <div className="my-2 space-y-0.5">
      <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground">
        <Scale className="size-3.5" />
        <span>{data.message}</span>
        <div className="flex gap-1 ml-auto">
          {Object.entries(counts).map(([decision, count]) => {
            const style = DECISION_STYLES[decision] ?? DECISION_STYLES.NotApplicable
            return (
              <Badge key={decision} variant="outline" className={cn('text-[9px] px-1 py-0', style)}>
                {decision}: {count}
              </Badge>
            )
          })}
        </div>
      </div>
      <div className="rounded-lg border border-border dark:border-border/40 bg-card shadow-sm overflow-hidden divide-y divide-border/50">
        {visible.map((entry) => (
          <DecisionLogRow key={entry.id} entry={entry} />
        ))}
      </div>
      {needsCollapse && !expanded && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(true)}
        >
          <ChevronDown className="size-3.5" />
          Show {hiddenCount} more decisions
        </Button>
      )}
    </div>
  )
}

function DecisionLogRow({ entry }: { entry: DecisionLogEntryData }) {
  const decisionStyle = DECISION_STYLES[entry.decision] ?? DECISION_STYLES.NotApplicable

  return (
    <div className="flex items-center gap-3 px-3.5 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0 font-semibold', decisionStyle)}>
            {entry.decision}
          </Badge>
          {entry.subjectId && (
            <span className="text-xs font-mono text-foreground truncate">{entry.subjectId}</span>
          )}
          {entry.resourceId && (
            <span className="text-xs text-muted-foreground">→ {entry.resourceId}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
          {entry.policyName && <span className="font-medium">{entry.policyName}</span>}
          {entry.action && <span>{entry.action}</span>}
          <span className="flex items-center gap-1">
            <Clock className="size-2.5" />
            {new Date(entry.timestamp).toLocaleString()}
          </span>
        </div>
        {entry.reason && (
          <div className="mt-0.5 text-[11px] text-muted-foreground italic truncate">{entry.reason}</div>
        )}
      </div>
    </div>
  )
}
